import { IModel, Collection, IViolation } from 'app/model/base/annotation';
import { Metamodel } from 'app/model/base/model';

import { Named, Owned } from 'app/model/base/named';
import { Folder, OwnedSet, IParameterizable, Parameter, format, ParameterValue, getGeneralNamePatterns, ActionConstants, IOwnedViolation, Dataset, ModelConstants } from 'shell-models-lib';
import { IWranglingDataFlowValidationResponse } from 'app/services/wrangling-dataflow.service.models';
import { DataFlowSink } from './dataflow-sink';
import { DataFlowParameter } from './dataflow-parameter';
import { DataFlow } from './dataflow';
import { PowerQuerySource } from './powerquery-source';
import { MessagingService, MessagingServiceChannel } from 'app/services/messaging/messaging-service';
import { Message, MessageKind } from 'app/services/messaging/message';
import { RUN_PARAMETERS_STORAGE_KEY } from 'app/services/globals';
import { localStorageGet } from 'app/shared/utils/window.util';

@Metamodel.model({
    name: ModelConstants.powerquery,
    display: [stringResources.PowerQuery],
    reusable: true,
    referable: true,
    constraints: {
        name: {
            required: true,
            patterns: getGeneralNamePatterns(stringResources.PowerQuery),
            uniqueModelName: true
        }
    },
    validator: WranglingDataflow.prototype.validate,
    tags: { gitValidator: WranglingDataflow.prototype.gitValidate },
    accessPublishConstraint: {
        writeResource: {
            action: ActionConstants.dataFlowsWrite
        },
        deleteResource: {
            action: ActionConstants.dataFlowsDelete
        }
    }
})
export class WranglingDataflow extends Named implements IParameterizable {
    public static readonly MODEL: IModel;

    @Metamodel.attribute({
        type: PowerQuerySource.MODEL,
        collection: Collection.Set
    })
    public sources = OwnedSet.create<PowerQuerySource>(this);

    @Metamodel.attribute({
        type: Parameter.MODEL,
        collection: Collection.Set,
        constraint: {
            unique: true,
            validator: DataFlow.validateParameters
        }
    })
    public readonly parameters = OwnedSet.create<DataFlowParameter>(this);

    // Map of source datasets to parameter values for authoring canvas
    public runParameters: Map<string, ParameterValue[]> = new Map<string, ParameterValue[]>();

    // Empty set required for other components that treat WranglingDataFlow as DataFlow
    public readonly cacheSinks = OwnedSet.create<DataFlowSink>(this);

    public folder: Folder = Owned.create(Folder.MODEL, this);
    public script: string;
    public documentLocale: string;
    public showDataPreview: boolean = true;

    public validationResponse: IWranglingDataFlowValidationResponse;

    constructor(name?: string, description?: string) {
        super(name, description);
    }

    public isSourceDatasetParameterized(): boolean {
        return !!this.sources.find((dataflowSource) => {
            return dataflowSource.sourceDataset.parameters && dataflowSource.sourceDataset.parameters.size > 0;
        });
    }

    public validate(): IViolation[] | undefined {
        const violations: IViolation[] = [];
        if (this.validationResponse && !this.validationResponse.isValid) {
            violations.push({
                context: this,
                message: () => this.validationResponse.message
            });
        }
        if (this.sources.size === 0) {
            violations.push({
                context: this,
                message: () => stringResources.PowerQuery_ValidationNoSourceDatasets
            });
        }
        return violations;
    }

    public gitValidate(powerquery: WranglingDataflow): IOwnedViolation[] | undefined {
        const violations: IOwnedViolation[] = [];
        return violations;
    }

    public areParametersConfigured(services: { messagingService: MessagingService }): boolean {
        if (this.sources && this.sources.size > 0) {
            let allParamsConfigured = true;
            this.sources.forEach((source) => {
                if (source.parameters) {
                    const runParameters = this.runParameters.get(source.sourceDataset.name);
                    source.parameters.forEach((param) => {
                        if (runParameters) {
                            const paramValue = runParameters.find((runParam) => runParam.parameter.name === param.name);
                            if (!paramValue || (!paramValue.value && !paramValue.parameter.defaultValue)) {
                                allParamsConfigured = false;
                            }
                        } else if (!param.defaultValue) {
                            allParamsConfigured = false;
                        }
                    });
                }
            });
            if (!allParamsConfigured) {
                services.messagingService.broadcast(MessagingServiceChannel.OpenPowerQueryParametersPanel, new Message(MessageKind.Field, this.name));
            }
            return allParamsConfigured;
        }
        return false;
    }

    public areDatasetsConfigured(): boolean {
        if (this.sources && this.sources.size > 0) {
            let allDatasetsConfigured = true;
            this.sources.forEach((source) => {
                if (!source.sourceDataset) {
                    allDatasetsConfigured = false;
                }
            });
            return allDatasetsConfigured;
        }
        return false;
    }

    public syncRunParametersForDataset(dataset: Dataset): void {
        const key = dataset.name;
        const value: ParameterValue[] = [];
        dataset.parameters.forEach((parameter) => {
            const authParam = Owned.create<ParameterValue>(ParameterValue.MODEL, this);
            authParam.parameter = parameter;
            const parametersStorageKey = format(RUN_PARAMETERS_STORAGE_KEY, key, parameter.name);
            const localStorageValue = localStorageGet(parametersStorageKey);
            authParam.value = localStorageValue ? localStorageValue : parameter.defaultValue;
            value.push(authParam);
        });
        this.runParameters.set(key, value);
    }
}
